import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { trpc } from "@/lib/trpc";
import { STALE_TIMES } from "@/lib/query-performance.js";
import { useOptionalProject } from "@/components/project-context";
import { features } from "@/lib/features";

interface Step {
  id: string;
  label: string;
  done: boolean;
  href: string;
}

function ProgressRing({
  percent,
  size = 36,
  stroke = 3,
}: {
  percent: number;
  size?: number;
  stroke?: number;
}) {
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <svg width={size} height={size} className="shrink-0 -rotate-90">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        className="text-primary-foreground"
        opacity={0.15}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className="text-primary-foreground transition-all duration-500"
      />
    </svg>
  );
}

export function SetupProgress({ onNavigate }: { onNavigate?: () => void }) {
  const project = useOptionalProject();

  const { data: sources, isLoading: sourcesLoading } =
    trpc.source.list.useQuery(
      { projectId: project?.id ?? "" },
      {
        enabled: !!project && features.knowledgeSources,
        staleTime: STALE_TIMES.list,
      },
    );

  const { data: personas, isLoading: personasLoading } =
    trpc.persona.list.useQuery(
      { projectId: project?.id ?? "" },
      { enabled: !!project, staleTime: STALE_TIMES.list },
    );

  // Count outlines — what the Scenarios page lists — not persona bindings.
  const { data: scenarios, isLoading: scenariosLoading } =
    trpc.outline.list.useQuery(
      { projectId: project?.id ?? "" },
      { enabled: !!project, staleTime: STALE_TIMES.list },
    );

  const { data: walkthroughs, isLoading: walkthroughsLoading } =
    trpc.walkthrough.listByProject.useQuery(
      { projectId: project?.id ?? "" },
      { enabled: !!project, staleTime: STALE_TIMES.list },
    );

  if (!project) return null;

  // Don't render until all data has loaded — otherwise all steps appear
  // incomplete during loading, causing a flash of the setup card
  if (
    (features.knowledgeSources && sourcesLoading) ||
    personasLoading ||
    scenariosLoading ||
    walkthroughsLoading
  )
    return null;

  const sourceItems = sources?.items ?? [];
  const sourceCount = sourceItems.length;
  const readyCount = sourceItems.filter((s) => s.status === "ready").length;
  const personaCount = personas?.length ?? 0;
  const scenarioCount = scenarios?.length ?? 0;
  const completedWalkthroughs =
    walkthroughs?.filter((w) => w.status === "completed") ?? [];
  const walkthroughCount = completedWalkthroughs.length;

  const hasSources = sourceCount > 0;
  const sourcesReady = hasSources && readyCount === sourceCount;
  const hasPersonas = personaCount > 0;
  const hasScenarios = scenarioCount > 0;
  const hasWalkthrough = walkthroughCount > 0;

  const steps: Step[] = [
    ...(features.knowledgeSources
      ? [
          {
            id: "sources",
            label: hasSources
              ? sourcesReady
                ? `${sourceCount} ${sourceCount === 1 ? "source" : "sources"} added`
                : `${readyCount}/${sourceCount} sources ready`
              : "Add knowledge sources",
            done: hasSources,
            href: `/projects/${project.id}/sources`,
          },
        ]
      : []),
    {
      id: "personas",
      label: hasPersonas
        ? `${personaCount} ${personaCount === 1 ? "persona" : "personas"} created`
        : "Generate personas",
      done: hasPersonas,
      href: `/projects/${project.id}/personas`,
    },
    {
      id: "scenarios",
      label: hasScenarios
        ? `${scenarioCount} ${scenarioCount === 1 ? "scenario" : "scenarios"} created`
        : "Create a scenario",
      done: hasScenarios,
      href: `/projects/${project.id}/scenarios`,
    },
    {
      id: "walkthrough",
      label: "Run your first walkthrough",
      done: hasWalkthrough,
      href: `/projects/${project.id}/scenarios`,
    },
    {
      id: "report",
      label: "View your first report",
      done: hasWalkthrough,
      href: hasWalkthrough
        ? `/projects/${project.id}/walkthroughs/${completedWalkthroughs[0]?.id}`
        : `/projects/${project.id}/walkthroughs`,
    },
  ];

  const doneCount = steps.filter((s) => s.done).length;
  const percent = Math.round((doneCount / steps.length) * 100);

  // Hide when all steps are complete
  if (doneCount === steps.length) return null;

  return (
    <div className="mx-2.5 mb-2 rounded-xl bg-primary p-3.5 text-primary-foreground shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-2.5">
        <ProgressRing percent={percent} />
        <div className="min-w-0">
          <p className="text-sm font-semibold">Project setup</p>
          <p className="text-xs tabular-nums text-primary-foreground">
            {doneCount} of {steps.length} complete
          </p>
        </div>
      </div>

      {/* Steps */}
      <div className="mt-3 space-y-1">
        {steps.map((step) => (
          <Link
            key={step.id}
            to={step.href}
            onClick={onNavigate}
            className={cn(
              "flex items-center gap-2 rounded-md px-1.5 py-1 text-xs transition-colors",
              step.done
                ? "text-primary-foreground"
                : "text-primary-foreground hover:bg-primary-foreground/10",
            )}
          >
            {step.done ? (
              <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary-foreground/25">
                <Check className="h-2.5 w-2.5" />
              </span>
            ) : (
              <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-primary-foreground/40" />
            )}
            {step.label}
          </Link>
        ))}
      </div>
    </div>
  );
}
